import { whom } from '../core/words.js';
import { SHORTFALLS } from '../data/resources.js';
import { STAFF_BY_ID, gatekeepersFor } from '../data/staff.js';
import { belowRung, onRung } from './standing.js';
import { penaltyFor } from './writeups.js';
import { windowCovers } from './npc.js';
import { createRng, hashSeed } from './rng.js';
import { dueIn, spotFor } from './tasks.js';

/**
 * Ask, trade, take.
 *
 * Three ways out of every shortfall and they are priced in three different
 * currencies, which is the whole design:
 *
 *   ASK    costs nothing now and writes a line in the ledger. The holder
 *          decides when to collect, and they will not pick a convenient moment.
 *   TRADE  costs minutes, immediately, and nothing else. It is the honest
 *          option and it is the one you cannot afford when you are behind.
 *   TAKE   costs nothing at all, unless somebody sees you — and some of them
 *          notice afterwards even when nobody did.
 *
 * Nothing here knows a name. Every rule is read off the `price` block in
 * `staff.js`, so the thirteen can be replaced wholesale without touching this
 * file. What it does know is that the three options have to stay genuinely
 * different: the moment one of them is strictly best, the middle game is a
 * button rather than a decision.
 */

export const OPTIONS = Object.freeze(['ask', 'trade', 'take']);

/**
 * What this person sees when they look you up: the ledger, less your card.
 *
 * Standing is what you earned and the penalty is what the write-ups against
 * your name are doing to it this week. They are kept apart because they expire
 * differently — one is moved by what you do and the other comes off on a date
 * printed on the card — and because a penalty applied *to* the ledger would
 * have to be given back by somebody remembering to, which is the kind of
 * bookkeeping that is wrong once and then wrong forever.
 *
 * How heavily this particular person weighs it is on their row. Nothing here
 * knows whose row.
 */
function seenBy(person, standing, strikes) {
  if (!standing) return null;
  return standing.get(person.department) - penaltyFor(person, strikes);
}

/**
 * Whether a door on this option is open, and whether asking is the offence.
 *
 * The only two things standing is allowed to do to a negotiation, and both are
 * doors rather than dials: an option is offered or it is not, and an ask is an
 * ask or it is an incident. What either one *costs* is a constant on the row
 * and cannot be reached from here — there is nothing in this file that could
 * scale a number by how anybody feels, and a test in `procurement.test.js`
 * fails if one ever appears.
 *
 * Which is also why being in trouble arrives here as a number subtracted from
 * standing rather than as a rule of its own. A card shuts the doors standing
 * shuts, at the step standing shuts them, and there is nothing in this file
 * that has heard of a write-up.
 *
 * With no `standing` to read, every door is open. That is deliberate: the
 * headless scripts and half the tests build a person and a shortfall and
 * nothing else, and a door that defaults shut would quietly make them all
 * measure a refusal.
 */
function doorFor(rule, value) {
  const shut = Boolean(rule.opensAt) && value !== null && !onRung(value, rule.opensAt);
  const reports =
    Boolean(rule.reportsYou) ||
    (Boolean(rule.reportsBelow) && value !== null && !onRung(value, rule.reportsBelow));
  return { shut, reports };
}

/** Every person who could resolve this shortfall, and whether they are in. */
export function holdersFor(shortfall, clock) {
  return gatekeepersFor(shortfall.owner).map((person) => ({
    person,
    present: windowCovers(person.window, clock.minutes),
  }));
}

/**
 * What one person will do about one shortfall, right now.
 *
 * Refusals come back as entries rather than being filtered out, because "the
 * Chef does not do favours" is information the player needs and a menu with one
 * item on it teaches nothing about why.
 */
export function optionsFor(person, entry, { standing, strikes = 0 } = {}) {
  const shortfall = SHORTFALLS[entry.id];
  const price = person.price ?? {};
  const value = seenBy(person, standing, strikes);

  return OPTIONS.map((kind) => {
    const rule = price[kind] ?? {};
    const door = doorFor(rule, value);
    if (!rule.allowed || door.shut) {
      return { kind, allowed: false, reason: rule.refusal ?? 'Not from them.' };
    }

    if (kind === 'ask') {
      return {
        kind,
        allowed: true,
        detail: rule.callsIn === false ? 'Free. She will not call it in.' : 'Free now. Owed later.',
        callsIn: rule.callsIn !== false,
        /*
         * Deliberately no tell, and now there are two things it is not telling
         * you: whether they will actually do it, and whether asking is itself
         * the thing that costs you. Both are learned by asking, which is the
         * only way either of them is worth anything.
         */
        reportsYou: door.reports,
        certain: rule.deliversOdds === undefined,
      };
    }

    if (kind === 'trade') {
      return {
        kind,
        allowed: true,
        detail: rule.exact
          ? `An errand first. ${rule.minutes} minutes, and he means it.`
          : `An errand first. About ${rule.minutes} minutes.`,
        minutes: rule.minutes ?? 20,
        exact: Boolean(rule.exact),
      };
    }

    const floor = rule.floorTo ?? -60;
    const current = value ?? 0;
    return {
      kind,
      allowed: true,
      detail:
        current <= floor
          ? 'Free unless somebody sees you. They already think the worst.'
          : 'Free unless somebody sees you.',
      floorTo: floor,
      noticed: Boolean(rule.noticed),
    };
  });
}

/**
 * Picks the errand a trade sends you on.
 *
 * Always somebody else's post, never the giver's own — the point is the walk.
 * Chosen off a seeded stream so the same trade with the same person on the same
 * day sends you to the same place, which makes the cost predictable enough to
 * plan around on a second run.
 */
export function errandFor(person, rng) {
  /*
   * Somebody else's post, and never one of the crew.
   *
   * The section is not a supply question and no negotiation is allowed to learn
   * their names — which includes being used as scenery for one. A trade that
   * sent you across the ballroom to find a banquet server was procurement
   * reaching into the one department it has no business in, and it picked a
   * post inside a room rather than at the end of a corridor, so the errand also
   * stopped being a walk and started being a wall.
   */
  const elsewhere = Object.values(STAFF_BY_ID).filter(
    (candidate) =>
      candidate.department !== person.department && !candidate.enforces && !candidate.crew
  );
  const target = rng.pick(elsewhere);
  return spotFor(target.id);
}

export function createProcurement({
  standing,
  favors,
  tasks,
  clock,
  rng,
  seed = 'lion',
  /**
   * How many live write-ups are against your name, asked for rather than held.
   *
   * A function because the card changes under this module constantly — one
   * ages off, one is filed mid-negotiation — and a number captured at
   * construction is the number the game happened to have at boot.
   */
  strikes = () => 0,
  onWriteUp,
  onSay,
}) {
  /**
   * Thefts nobody saw, waiting to be noticed.
   *
   * A take that goes unseen is not free for everyone — some of them count the
   * stock at the end of the night. The cost lands at the start of the next
   * shift, far enough from the act that you have to remember doing it.
   */
  let pending = [];

  function resolveEntry(entry, via) {
    entry.resolved = true;
    entry.via = via;
  }

  /**
   * Whether somebody who says yes to everything actually does it, this time.
   *
   * Rolled off a stream keyed on the person, the shortfall and the day, so the
   * answer is fixed for that day: asking him twice gets the same shrug. A live
   * roll would make him a slot machine you pull until it pays, which is not
   * unreliable, it is free.
   */
  function delivers(person, entry, rule) {
    if (rule.deliversOdds === undefined) return true;
    const roll = createRng(hashSeed(seed, 'delivers', person.id, entry.id, clock.day));
    return roll.next() < rule.deliversOdds;
  }

  return {
    get pending() {
      return pending;
    },

    optionsFor,
    holdersFor,

    /**
     * What it costs when somebody names you for helping yourself to their
     * stock. Null if that person has nothing to be robbed of.
     *
     * Read off the same row that says whether asking is allowed, so a
     * department that is relaxed about being asked is relaxed about being
     * caught, and the file that holds the thirteen is still the only place any
     * of it is written down.
     */
    priceOfBeingNamed(staffId) {
      const person = STAFF_BY_ID[staffId];
      const rule = person?.price?.take;
      if (!rule) return null;
      return rule.floorTo ?? -60;
    },

    /**
     * Does the thing.
     *
     * It does not ask whether anybody was looking. Nothing in the game does
     * except perception, and what a witness makes of it is decided by whoever
     * is holding the suspicion.
     */
    resolve({ person, entry, kind, beo = null }) {
      const shortfall = SHORTFALLS[entry.id];
      const rule = person.price?.[kind] ?? {};
      /*
       * Asked again here rather than trusted from the menu.
       *
       * The menu was drawn on some earlier frame and the card can have moved
       * since — being written up in front of the person you are mid-sentence
       * with is an ordinary thing to have happen — and a door that was open
       * when it was drawn must not still be open when it is pressed.
       */
      const door = doorFor(rule, seenBy(person, standing, strikes()));
      if (!rule.allowed || door.shut) {
        return { ok: false, reason: rule.refusal ?? 'Not from them.' };
      }
      if (entry.resolved) return { ok: false, reason: 'Already covered.' };

      if (kind === 'ask') {
        if (door.reports) {
          /*
           * Asking was the offence, and the shortfall is exactly where it was.
           *
           * Who it costs is on the row rather than assumed to be the person who
           * picked up the phone: somebody procedural reports *upwards*, and the
           * cost lands with the department she reports to, which is also the
           * department whose opinion decides whether she has to report you at
           * all next time.
           */
          /*
           * Their own department is named as the reporter, and it is named
           * separately from whoever the cost lands on. Those are two different
           * departments here on purpose — she reports *upwards* — and the one
           * that must not profit is hers, not his.
           */
          onWriteUp?.('reported', `${person.name}: asked for ${shortfall.label.toLowerCase()}`, {
            by: person.department,
          });
          standing.applyDirect(
            { [rule.reportsTo ?? person.department]: -8 },
            { reason: `${person.name} reported you` }
          );
          return { ok: false, reported: true, reason: 'She picked up the phone.' };
        }
        /*
         * And the other way an ask can come to nothing: somebody who said yes
         * and meant it and is not going to get to it. No debt, no standing, no
         * write-up — the shortfall is still open and the only thing it cost you
         * is the walk over here.
         */
        if (!delivers(person, entry, rule)) {
          return { ok: false, failed: true, reason: 'Yeah, no. It did not happen. Sorry.' };
        }
        resolveEntry(entry, 'ask');
        if (rule.callsIn !== false) {
          favors.owe({
            staffId: person.id,
            department: person.department,
            day: clock.day,
            coolDays: rule.coolDays ?? 1,
            weight: rule.weight ?? 1,
            note: shortfall.label,
            beoId: beo?.id ?? null,
          });
        }
        return { ok: true, via: 'ask', owed: rule.callsIn !== false };
      }

      if (kind === 'trade') {
        /*
         * One errand per thing you are short of. A second press at the same
         * person sent a new starter on a second errand, then a third, until
         * they had too much on and every one of them ran out.
         */
        const running = tasks.list?.find((t) => t.shortfallId === entry.id && !t.expired);
        if (running) return { ok: false, reason: `You are already on it: ${running.label.replace(/^[^:]*: /, '')}.` };
        if (tasks.full) return { ok: false, reason: 'You have too much on already.' };
        const spot = errandFor(person, rng);
        const minutes = rule.minutes ?? 20;
        const task = {
          id: `trade-${person.id}-${clock.day}-${Math.round(clock.minutes)}`,
          source: 'trade',
          staffId: person.id,
          department: person.department,
          label: `${person.name}: go to ${whom(spot.label)}`,
          target: spot,
          /*
           * Twice the estimate, so the errand is a cost and not a coin toss —
           * unless the person quoting it is the one whose estimates are the
           * truth, in which case the number he said is the number you get. It
           * is the longest errand on the roster and the only one with no slack
           * in it, which is the same character trait read from both ends.
           */
          dueAbsolute: dueIn(clock, rule.exact ? minutes : minutes * 2),
          minutes,
          shortfallId: entry.id,
          beoId: beo?.id ?? null,
        };
        tasks.add(task);
        return { ok: true, via: 'trade', task };
      }

      /*
       * --- take -------------------------------------------------------------
       *
       * Nothing here asks whether anybody was looking, and that is the point of
       * the whole rewrite. This used to take a `seen` flag, and on the strength
       * of it hand out a write-up and floor a department on the spot — a second
       * answer to "did they notice", with its own idea of range and walls, next
       * to the one the restricted zones used.
       *
       * Now you take the thing. Somebody either witnessed it or did not, and if
       * one of them gets sure enough to say so, `priceOfBeingNamed` is what it
       * costs. The stock count not adding up in the morning is separate and
       * still here: that is an audit, not a pair of eyes.
       */
      resolveEntry(entry, 'take');
      if (rule.noticed) {
        pending = [
          ...pending,
          {
            department: person.department,
            staffId: person.id,
            cost: rule.noticeCost ?? 10,
            day: clock.day,
            note: shortfall.label,
          },
        ];
      }

      /*
       * Two things a person can do about their own stock going missing that are
       * not an audit and are not a phone call to Management.
       *
       * `dropsRung` is somebody who does not need to tell anybody. She knows
       * what was on her cart. It costs you the step you were on rather than a
       * number, so it costs a new hire and a favourite the same *thing* — and
       * for a department that starts above nothing, it is the only way to lose
       * what you were given for free.
       *
       * `tells` is the opposite temperament: no report, because a report is an
       * admission he cannot run his own section, and instead everybody else in
       * the building hears about it by the end of service. A small hit to all
       * six of the others, for nothing you did to any of them.
       *
       * Neither asks whether anybody was looking. That is still perception's
       * question and this is still not the file that answers it: these are
       * people noticing their own stock is short, which is arithmetic.
       */
      if (rule.dropsRung) {
        standing.floorTo(person.department, belowRung(standing.get(person.department)), {
          reason: `${person.name} knows what was on that cart`,
        });
      }
      if (rule.tells) {
        const word = Object.fromEntries(
          standing.ids
            .filter((id) => id !== person.department)
            .map((id) => [id, -rule.tells])
        );
        standing.applyDirect(word, { reason: `${person.name} told everybody` });
      }

      return {
        ok: true,
        via: 'take',
        noticed: Boolean(rule.noticed),
        dropped: Boolean(rule.dropsRung),
        told: Boolean(rule.tells),
      };
    },

    /**
     * Settles up the thefts that went unseen.
     *
     * Called when a new day starts. Returns what was discovered so the summary
     * can say it out loud — a cost that arrives silently is a cost the player
     * learns nothing from.
     */
    settlePending(day) {
      const due = pending.filter((entry) => entry.day < day);
      if (!due.length) return [];
      pending = pending.filter((entry) => entry.day >= day);
      for (const entry of due) {
        standing.applyDirect(
          { [entry.department]: -entry.cost },
          { reason: `${STAFF_BY_ID[entry.staffId].name} counted the stock` }
        );
      }
      return due;
    },

    clear() {
      pending = [];
    },

    toJSON: () => ({ pending }),
    load(state) {
      pending = [...(state?.pending ?? [])];
    },
  };
}
